import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../api/services';

interface Notification {
  _id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: string;
  metadata?: Record<string, any>;
}

/** Resolve navigation link from notification type + metadata.
 *  This way routes can change without breaking old notifications. */
function resolveLink(n: Notification): string | null {
  const m = n.metadata || {};
  const taskId = typeof m.taskId === 'object' ? m.taskId.$oid || m.taskId : m.taskId;
  const projectId = typeof m.projectId === 'object' ? m.projectId.$oid || m.projectId : m.projectId;
  const featureId = typeof m.featureId === 'object' ? m.featureId.$oid || m.featureId : m.featureId;

  switch (n.type) {
    case 'task_assigned':
    case 'task_completed':
      if (taskId) return `/employee/dashboard?taskId=${taskId}`;
      break;
    case 'feature_submitted':
    case 'feature_approved':
    case 'feature_rejected':
      if (featureId) return `/employee/dashboard?featureId=${featureId}`;
      if (projectId) return `/employee/dashboard?projectId=${projectId}`;
      break;
    case 'approval_requested':
    case 'approval_approved':
    case 'approval_rejected':
    case 'approval_escalated':
      return `/admin/dashboard?tab=approvals`;
    case 'evaluation_opened':
      return `/employee/dashboard`;
    case 'sp_awarded':
      return `/employee/dashboard`;
  }
  // Fallback: use stored link (normalize old ?task= to ?taskId=)
  if (n.link) return n.link.replace(/[?&]task=/, (match) => match.replace('task=', 'taskId='));
  return null;
}

const TYPE_ICONS: Record<string, { icon: string; color: string }> = {
  task_assigned: { icon: '📋', color: 'bg-blue-50 text-blue-600' },
  task_completed: { icon: '✅', color: 'bg-green-50 text-green-600' },
  evaluation_opened: { icon: '📝', color: 'bg-purple-50 text-purple-600' },
  approval_requested: { icon: '🔔', color: 'bg-yellow-50 text-yellow-700' },
  approval_approved: { icon: '✅', color: 'bg-green-50 text-green-600' },
  approval_rejected: { icon: '❌', color: 'bg-red-50 text-red-600' },
  approval_escalated: { icon: '⬆️', color: 'bg-orange-50 text-orange-600' },
  sp_awarded: { icon: '⭐', color: 'bg-yellow-50 text-yellow-600' },
  feature_submitted: { icon: '📦', color: 'bg-indigo-50 text-indigo-600' },
  feature_approved: { icon: '✅', color: 'bg-green-50 text-green-600' },
  feature_rejected: { icon: '❌', color: 'bg-red-50 text-red-600' },
  general: { icon: '📢', color: 'bg-gray-50 text-gray-600' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface Props {
  onUnreadCountChange?: (count: number) => void;
}

const NotificationDropdown: React.FC<Props> = ({ onUnreadCountChange }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationAPI.getUnreadCount();
      const count = res.data?.count ?? 0;
      setUnreadCount(count);
      onUnreadCountChange?.(count);
    } catch { /* silent */ }
  }, [onUnreadCountChange]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getMyNotifications({ limit: 20 });
      setNotifications(res.data?.data ?? []);
      setUnreadCount(res.data?.unreadCount ?? 0);
      onUnreadCountChange?.(res.data?.unreadCount ?? 0);
    } catch { /* silent */ }
    setLoading(false);
  }, [onUnreadCountChange]);

  // Poll unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Load full list when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = async (n: Notification) => {
    // Mark as read and remove from the dropdown
    if (!n.read) {
      try {
        await notificationAPI.markAsRead(n._id);
      } catch (err: any) {
        console.error('[NOTIFICATION] markAsRead failed:', n._id, err?.response?.status, err?.response?.data);
      }
      setUnreadCount(prev => Math.max(0, prev - 1));
      onUnreadCountChange?.(Math.max(0, unreadCount - 1));
    }
    setNotifications(prev => prev.map(x => x._id === n._id ? { ...x, read: true } : x));

    const link = resolveLink(n);
    if (link) {
      navigate(link);
      const url = new URL(link, window.location.origin);
      const taskId = url.searchParams.get('taskId');
      const projectId = url.searchParams.get('projectId');
      if (taskId || projectId) {
        window.dispatchEvent(new CustomEvent('notification-navigate', { detail: { taskId, projectId } }));
      }
      setOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(x => ({ ...x, read: true })));
      setUnreadCount(0);
      onUnreadCountChange?.(0);
    } catch { /* silent */ }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await notificationAPI.deleteNotification(id);
      setNotifications(prev => prev.filter(x => x._id !== id));
      const deleted = notifications.find(n => n._id === id);
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
        onUnreadCountChange?.(Math.max(0, unreadCount - 1));
      }
    } catch { /* silent */ }
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[480px] bg-white rounded-xl shadow-xl border border-gray-200 z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              notifications.map(n => {
                const typeInfo = TYPE_ICONS[n.type] || TYPE_ICONS.general;
                return (
                  <div
                    key={n._id}
                    onClick={() => handleClick(n)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 transition-colors group ${
                      n.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/40 hover:bg-blue-50/60'
                    }`}
                  >
                    {/* Icon */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5 ${typeInfo.color}`}>
                      {typeInfo.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-snug ${n.read ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
                          {n.message}
                        </p>
                        {/* Delete button */}
                        <button
                          onClick={(e) => handleDelete(e, n._id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-gray-500 flex-shrink-0 mt-0.5 transition-opacity"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(n.createdAt)}</p>
                    </div>

                    {/* Unread dot */}
                    {!n.read && (
                      <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
